import { Link } from "react-router-dom";
import { Check, ArrowRight, GraduationCap, HeartHandshake, Syringe, PhoneCall } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { SITE, IMG } from "../data/siteData";

const curriculum = [
  "Loose leash walking",
  "On-leash heel",
  "Sit",
  "Down",
  "Stay",
  "Place command",
  "Crate training",
  "Healthy exposure and socialization",
];

const curbing = [
  "Over-excitement",
  "Counter surfing",
  "Jumping",
  "Leash pulling",
  "Barking",
  "Lunging and barking on-leash",
];

export default function Jumpstart() {
  return (
    <div data-testid="jumpstart-page">
      <SEO
        title="Puppy Jumpstart Program | Chinook K9 Dog Training Calgary"
        description="2-week board and train jumpstart program for puppies and young dogs 4 months to 1 year. On-leash obedience, manners and socialization near Calgary, AB."
        path="/services/jumpstart"
      />

      <section className="relative min-h-[70vh] flex items-end overflow-hidden" data-testid="js-hero">
        <img src={IMG.jumpstartWide} alt="Puppy jumpstart program at Chinook K9" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-black/25 to-black/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 pt-40 w-full">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.3em] text-xs uppercase mb-4 hero-text-shadow">The Jumpstart Program</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-white max-w-3xl leading-[1.05] hero-text-shadow">
              Build healthy habits from day one
            </h1>
            <p className="mt-5 text-white/85 max-w-2xl leading-relaxed hero-text-shadow">
              Looking to help your young dog build healthy habits and proactively avoid future behavioural issues?
              Then this is the program for you.
            </p>
            <p className="mt-6 inline-block bg-brand text-white font-heading font-extrabold text-2xl px-7 py-3 rounded-full shadow-[0_0_20px_rgba(220,38,38,0.45)]" data-testid="js-price">
              2-Week Program · $1,950
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-24" data-testid="js-curriculum">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6">A solid foundation in 2 weeks</h2>
            <p className="text-white/70 leading-relaxed mb-7">
              The Jumpstart Program is a 2-week board and train focused on providing your dog or puppy
              (4 months – 1 year old) a solid foundation of on-leash communication, manners and socialization, including:
            </p>
            <div className="grid grid-cols-2 gap-2.5" data-testid="js-curriculum-list">
              {curriculum.map((c) => (
                <div key={c} className="flex items-center gap-2 text-sm text-white/75">
                  <Check size={15} className="text-brand shrink-0" /> {c}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 h-full">
              <h3 className="font-heading text-lg font-bold text-white mb-5">The program also focuses on curbing unwanted behaviours</h3>
              <ul className="space-y-3" data-testid="js-curbing-list">
                {curbing.map((c) => (
                  <li key={c} className="flex gap-2.5 text-sm text-white/75">
                    <Check size={16} className="text-brand shrink-0 mt-0.5" /> {c}
                  </li>
                ))}
              </ul>
              <p className="text-white/50 text-sm mt-6 leading-relaxed">
                For the duration of the program, your dog stays with us in our training facility — receiving one-on-one
                education daily, plus structured socialization with other dogs, humans, and environments for a well-rounded stay.
              </p>
            </div>
          </Reveal>
        </div>
        <Reveal className="max-w-7xl mx-auto px-6 mt-10">
          <p className="text-white/50 text-sm italic leading-relaxed border-l-2 border-brand pl-5" data-testid="js-suitability-note">
            The Jumpstart Program is for young dogs up to a year old and is not suitable for dogs with behavioural
            concerns or off-leash goals. If you're dealing with behavioural concerns or want off-leash obedience, our{" "}
            <Link to="/services/board-and-train" className="text-brand hover:underline not-italic">Board and Train Program</Link> and{" "}
            <Link to="/services/private-training" className="text-brand hover:underline not-italic">Private Training Program</Link> can
            help you reach your goals.
          </p>
        </Reveal>
      </section>

      <section className="bg-white py-24 text-slate-900" data-testid="js-owner-education">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="max-w-3xl mb-12">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Owner Education</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Success continues at home</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Your dog will receive an enormous amount of education, life experience and training during their stay —
              however it will be up to you, the owner, to ensure your dog's success after they return home.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { icon: GraduationCap, title: "Included Sessions", desc: "One (1) Go-Home session (minimum 2 hours at pick-up) where we break down all training, plus one (1) Follow-Up Session (1 hour) to ensure the transition home is successful and troubleshoot if needed. Training equipment used during your dog's stay is provided." },
              { icon: HeartHandshake, title: "Lifetime Support + Benefits", desc: "Free consultation via phone, text or email after your dog's program, plus discounts on further training, day training services, and reduced-cost boarding following their program." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <div className="border border-black/10 rounded-2xl p-8 h-full hover:border-brand/50 hover:shadow-xl transition-[border-color,box-shadow]" data-testid={`js-edu-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-brand/10 text-brand flex items-center justify-center mb-5"><c.icon size={22} /></div>
                  <h3 className="font-heading text-lg font-bold mb-3">{c.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#121212] border-y border-white/10 py-20" data-testid="js-boarding-req">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <Syringe className="text-brand" size={26} />
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">Boarding Requirements</h2>
            </div>
            <p className="text-white/70 leading-relaxed mb-5">
              Proof of current vaccinations is required before boarding your dog. Vaccinations must be current for your dog's age:
            </p>
            <ul className="space-y-2.5">
              {["Rabies vaccine", "Bordetella", "Distemper (DHLPP)"].map((v) => (
                <li key={v} className="flex items-center gap-2.5 text-white/80 font-medium"><Check size={16} className="text-brand" /> {v}</li>
              ))}
            </ul>
            <p className="text-white/50 text-sm mt-5">The age range for our Jumpstart Program is 4 months to 1 year old.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-obsidian border border-white/10 rounded-2xl p-9 text-center">
              <h3 className="font-heading text-2xl font-bold text-white mb-3">The first step is just a click away</h3>
              <p className="text-white/60 text-sm mb-7">Unsure which program is best? Contact us — we'll help you decide.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" data-testid="js-contact-cta" className="bg-brand text-white font-heading font-bold px-8 py-3.5 rounded-full hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2">
                  Contact Us <ArrowRight size={16} />
                </Link>
                <a href={SITE.phoneHref} data-testid="js-call-cta" className="border border-white/25 text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2">
                  <PhoneCall size={16} /> {SITE.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
